import { createStore } from 'zustand/vanilla'
import { STATS_DEFAULT_ACTIVE_CARDS } from '@/config/demoConfig'

/**
 * Stats bar settings. Ported verbatim from the Pinia setup store (Phase 1B):
 * same defaults, same coercions, same seven localStorage keys — other code
 * (MapView, bootstrapSession) reads those keys by name.
 */
export type StatsSettingsState = {
  comparisonMode: boolean
  baselineScenarioId: number
  capacityCalculationMethod: string
  serviceLevelCalculationMethod: string
  /**
   * On by default. The app has always BEHAVED this way — MapView's init read
   * the persisted key with a default of true and overrode this store — while
   * the store, and the Customize Map switch bound to it, said false. The store
   * is the only owner now, so its default has to be the behaviour users had.
   */
  presentationMode: boolean
  activeCards: Record<string, boolean>
}

const ACTIVE_CARDS_VERSION = 'v2-seven-cards'

function defaults(): StatsSettingsState {
  return {
    comparisonMode: false,
    baselineScenarioId: 1,
    capacityCalculationMethod: 'average',
    serviceLevelCalculationMethod: 'volume_weighted',
    presentationMode: true,
    activeCards: { ...(STATS_DEFAULT_ACTIVE_CARDS as Record<string, boolean>) },
  }
}

/** loadFromLocalStorage(), as a pure function of storage → state. */
function fromStorage(): StatsSettingsState {
  const s = defaults()
  try {
    const savedComparisonMode = localStorage.getItem('statComparisonMode')
    if (savedComparisonMode !== null) s.comparisonMode = savedComparisonMode === 'true'
    const savedBaselineId = localStorage.getItem('baselineScenarioId')
    if (savedBaselineId !== null) s.baselineScenarioId = Number(savedBaselineId) || 1
    const savedCapacityMethod = localStorage.getItem('capacityCalculationMethod')
    if (savedCapacityMethod !== null) s.capacityCalculationMethod = savedCapacityMethod
    const savedServiceLevelMethod = localStorage.getItem('serviceLevelCalculationMethod')
    if (savedServiceLevelMethod !== null) s.serviceLevelCalculationMethod = savedServiceLevelMethod
    const savedPresentationMode = localStorage.getItem('statsPresentationMode')
    if (savedPresentationMode !== null) s.presentationMode = savedPresentationMode === 'true'
    // Saved cards are merged OVER the defaults, so a browser holding the old
    // set would keep showing retired cards. Discard anything from a previous
    // version of the card set.
    const savedCardsVersion = localStorage.getItem('activeStatCardsVersion')
    const savedActiveCards = localStorage.getItem('activeStatCards')
    if (savedCardsVersion !== ACTIVE_CARDS_VERSION) {
      s.activeCards = { ...(STATS_DEFAULT_ACTIVE_CARDS as Record<string, boolean>) }
      localStorage.setItem('activeStatCardsVersion', ACTIVE_CARDS_VERSION)
      localStorage.setItem('activeStatCards', JSON.stringify(s.activeCards))
    } else if (savedActiveCards !== null) {
      try {
        s.activeCards = { ...s.activeCards, ...JSON.parse(savedActiveCards) }
      } catch {
        /* a corrupt saved card set is ignored, as before */
      }
    }
  } catch {
    /* storage unavailable: defaults */
  }
  return s
}

export const statsSettingsStore = createStore<StatsSettingsState>(() => fromStorage())

function persist(s: StatsSettingsState) {
  try {
    localStorage.setItem('statComparisonMode', String(s.comparisonMode))
    localStorage.setItem('baselineScenarioId', String(s.baselineScenarioId))
    localStorage.setItem('capacityCalculationMethod', s.capacityCalculationMethod)
    localStorage.setItem('serviceLevelCalculationMethod', s.serviceLevelCalculationMethod)
    localStorage.setItem('statsPresentationMode', String(s.presentationMode))
    localStorage.setItem('activeStatCards', JSON.stringify(s.activeCards))
    localStorage.setItem('activeStatCardsVersion', ACTIVE_CARDS_VERSION)
  } catch {
    /* storage unavailable: in-memory only, as before */
  }
}

/** The Pinia store had no computeds. */
export const statsSettingsSelectors = {}

export const statsSettingsActions = {
  updateSettings(newSettings: Partial<Record<keyof StatsSettingsState, unknown>> | null | undefined) {
    if (!newSettings || typeof newSettings !== 'object') return
    const cur = statsSettingsStore.getState()
    const next: StatsSettingsState = { ...cur }
    if (newSettings.comparisonMode !== undefined) next.comparisonMode = Boolean(newSettings.comparisonMode)
    if (newSettings.baselineScenarioId !== undefined) next.baselineScenarioId = Number(newSettings.baselineScenarioId) || 1
    if (newSettings.capacityCalculationMethod !== undefined) next.capacityCalculationMethod = String(newSettings.capacityCalculationMethod || 'average')
    if (newSettings.serviceLevelCalculationMethod !== undefined) next.serviceLevelCalculationMethod = String(newSettings.serviceLevelCalculationMethod || 'volume_weighted')
    if (newSettings.presentationMode !== undefined) next.presentationMode = Boolean(newSettings.presentationMode)
    if (newSettings.activeCards !== undefined && typeof newSettings.activeCards === 'object' && newSettings.activeCards !== null) {
      next.activeCards = { ...cur.activeCards, ...(newSettings.activeCards as Record<string, boolean>) }
    }
    statsSettingsStore.setState(next, true)
    persist(next)
  },
  resetToDefaults() {
    const next = defaults()
    statsSettingsStore.setState(next, true)
    persist(next)
  },
  loadFromLocalStorage() {
    statsSettingsStore.setState(fromStorage(), true)
  },
  persistToLocalStorage() {
    persist(statsSettingsStore.getState())
  },
}

export function __resetStatsSettingsForTests() {
  statsSettingsStore.setState(fromStorage(), true)
}
